import {
  ADMIN_MESSAGE_FORMATS,
  isHoldAnnouncementFormat,
  isInvoiceResubmissionRequestedFormat,
  isPartAnnouncementFormat,
} from '../../../../constants/messageFormats';
import { EVENT_FINISHED_CHAT_PREVIEW } from './eventMessageFormatUtils';
import { sanitizeReminderContent } from './systemMessageUtils';

const normalizeText = (value) => String(value ?? '').replace(/\r\n?/g, '\n');
const trimText = (value) => normalizeText(value).trim();

const pushLine = (lines, value) => {
  const next = trimText(value);
  if (!next) return;
  lines.push(next);
};

const resolveReplyPreviewText = (reply = {}) => {
  const type = String(reply?.type || '').trim().toLowerCase();
  if (type === 'file') {
    return String(reply?.fileName || 'ファイル').trim();
  }
  if (type === 'image') {
    return '画像';
  }
  return String(reply?.previewText || '').trim();
};

const resolveReplyCopyLine = (reply = {}) => {
  if (!reply?.messageId) return '';
  const senderName = trimText(reply?.senderName) || '返信元';
  const previewText = resolveReplyPreviewText(reply);
  if (!previewText) return `返信先: ${senderName}`;
  return `返信先: ${senderName} / ${previewText}`;
};

const isAnnouncementEventFormat = (format) =>
  isHoldAnnouncementFormat(format) ||
  [
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED,
    ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED,
  ].includes(format);

const resolveEventMetaLines = (message = {}) => {
  const lines = [];
  const eventName = trimText(message?.event_name || message?.contract_event_name || message?.reminder_event_name);
  const performanceType = trimText(
    message?.event_performance_type ||
      message?.contract_event_performance_type ||
      message?.reminder_event_performance_type
  );
  const dateLabel = trimText(message?.event_date || message?.contract_event_date || message?.reminder_schedule_date);
  const timeLabel = trimText(
    message?.event_start_time || message?.contract_event_start_time || message?.reminder_start_time
  );
  const venueLabel = trimText(
    message?.event_venue || message?.contract_event_venue || message?.reminder_place_name
  );

  pushLine(lines, [eventName, performanceType].filter(Boolean).join(' '));
  pushLine(lines, [dateLabel, timeLabel].filter(Boolean).join(' '));
  pushLine(lines, venueLabel);
  return lines;
};

const resolveSystemFallbackTitle = (message = {}) => {
  const format = String(message?.message_format || '').trim().toLowerCase();
  if (isPartAnnouncementFormat(format)) {
    return 'お知らせが届きました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED) {
    return '中止保証金額が確定しました';
  }
  if (isInvoiceResubmissionRequestedFormat(format)) {
    return '請求書の再発行依頼が届きました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD) return 'オファーが届きました';
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED) return 'オファーがキャンセルされました';
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT) return '本契約が届きました';
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED) {
    return '契約が取り消されました。ご質問等はチャットにて行ってください。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED) {
    const content = trimText(message?.content);
    if (content.includes('再契約は不要')) {
      return content.includes('スケジュール')
        ? 'スケジュールが更新されました。内容を確認してください。'
        : '契約内容が更新されました。詳細を確認してください。';
    }
    return '契約内容が更新されました。更新内容をチェックし再度契約を結ぶ必要があります。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE) {
    return 'オファーのステータスが更新されました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONCIERGE_RESULT) {
    return 'コンシェルジュ回答が届きました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ADDITIONAL_REWARD_OFFER) {
    return '追加支払いの確認依頼が届きました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_ADDITIONAL_PAYMENT_CANCELLED) {
    return '追加支払いの確認依頼が取り消されました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.ADDITIONAL_REWARD_CLAIM_COMPLETED) {
    return '追加請求の審査が完了しました';
  }
  if (format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) {
    return 'スケジュールのお知らせ';
  }
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_CREATE) return 'ノートが作成されました。';
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_UPDATE) return 'ノートが更新されました。';
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_DELETE) return 'ノートが削除されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_CREATED) return 'タイムテーブルが作成されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_UPDATED) return 'タイムテーブルが更新されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO) return 'タイムテーブルの行メモが追加されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO_ADMIN) {
    return 'タイムテーブルの行メモ（管理者のみ）が追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT) {
    return 'タイムテーブルのお知らせが追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT_ADMIN) {
    return 'タイムテーブルのお知らせ（管理者のみ）が追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_MATERIAL_SHARED) return '共有資料がアーティストに共有されました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_IMAGES_ADDED) return 'イベントアルバムが共有されました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED) return 'イベントの情報が更新または追加されました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_PUBLISHED) return '情報公開がされました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_MEMBERS_PUBLISHED) return '参加メンバーが公開されました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED) return 'イベントが中止となりました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_FINISHED) return 'イベント終了のお知らせ';
  return 'システム通知が届きました。';
};

const resolveSystemBodyText = (message = {}) => {
  const format = String(message?.message_format || '').trim().toLowerCase();
  const rawContent = trimText(message?.content);
  if (format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) {
    return trimText(sanitizeReminderContent(message?.content));
  }
  if (rawContent) return rawContent;
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED) return 'このオファーはキャンセルされました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_FINISHED) return EVENT_FINISHED_CHAT_PREVIEW;
  return '';
};

const JAPANESE_WORD_PARTICLE_REGEX = /(への|について|から|まで|より|へ|を|に|で|が|は|の|と|も|や)/g;
const JAPANESE_WORD_PARTICLES = new Set(['への', 'について', 'から', 'まで', 'より', 'へ', 'を', 'に', 'で', 'が', 'は', 'の', 'と', 'も', 'や']);

const extractWordShortcutMatches = (text) => {
  const matches = [];
  const tokenRegex = /[A-Za-z0-9][A-Za-z0-9._-]{1,31}|[一-龠々ぁ-んァ-ヶー]{2,24}/g;
  let nextMatch = tokenRegex.exec(text);
  while (nextMatch) {
    const rawText = String(nextMatch[0] || '');
    const startIndex = nextMatch.index;
    if (/^[A-Za-z0-9]/.test(rawText)) {
      matches.push({
        text: rawText,
        start: startIndex,
        end: startIndex + rawText.length,
      });
    } else {
      const parts = rawText
        .replace(JAPANESE_WORD_PARTICLE_REGEX, '\u0000$1\u0000')
        .split('\u0000')
        .filter(Boolean);
      let localCursor = 0;
      parts.forEach((part) => {
        const nextOffset = rawText.indexOf(part, localCursor);
        if (nextOffset < 0) return;
        localCursor = nextOffset + part.length;
        if (JAPANESE_WORD_PARTICLES.has(part)) return;
        if (part.length < 2 || part.length > 12) return;
        matches.push({
          text: part,
          start: startIndex + nextOffset,
          end: startIndex + nextOffset + part.length,
        });
      });
    }
    nextMatch = tokenRegex.exec(text);
  }
  return uniqueMatches(matches).sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    if (a.end !== b.end) return a.end - b.end;
    return a.text.localeCompare(b.text, 'ja');
  });
};

const COPY_SHORTCUT_DEFINITIONS = [
  {
    key: 'link',
    pattern: /(?:https?:\/\/|www\.)[^\s\u3000<>()]+/gi,
  },
  {
    key: 'address',
    pattern:
      /(?:〒\s?\d{3}-\d{4}\s*(?:東京都|北海道|(?:京都|大阪)府|.{2,3}県).{0,24}?\d{1,4}[-ー]\d{1,4}(?:[-ー]\d{1,4})?|(?:東京都|北海道|(?:京都|大阪)府|.{2,3}県).{0,24}?\d{1,4}[-ー]\d{1,4}(?:[-ー]\d{1,4})?)/g,
  },
  {
    key: 'phone',
    pattern: /(?:\+?81[-\s]?)?(?:0\d{1,4}[-\s]?\d{1,4}[-\s]?\d{3,4})/g,
  },
  {
    key: 'name',
    pattern:
      /(?:[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+|[A-Z]{2,}(?:\s+[A-Z]{2,})+|[一-龠々]{1,4}\s+[一-龠々]{1,4}|[ァ-ヶー]{2,8}\s+[ァ-ヶー]{2,8}|[一-龠々ぁ-んァ-ヶ]{2,10}(?:様|さん|氏|くん|ちゃん))/g,
  },
  {
    key: 'word',
    resolveMatches: extractWordShortcutMatches,
  },
];

const uniqueMatches = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = `${item.start}:${item.end}:${item.text}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeShortcutMatch = (shortcutKey, rawText, startIndex) => {
  const normalizedText = trimText(rawText);
  if (!normalizedText) return null;
  if (shortcutKey === 'name' && normalizedText.endsWith('様')) {
    const trimmedText = normalizedText.slice(0, -1).trimEnd();
    if (!trimmedText) return null;
    return {
      text: trimmedText,
      start: startIndex,
      end: startIndex + trimmedText.length,
    };
  }
  return {
    text: normalizedText,
    start: startIndex,
    end: startIndex + rawText.length,
  };
};

const resolveTextMessageContent = (message = {}) => {
  const format = String(message?.message_format || '').trim().toLowerCase();
  if (format !== ADMIN_MESSAGE_FORMATS.TEXT) return '';
  return trimText(message?.content);
};

export const resolveCopyableChatMessageText = (message = {}) => {
  if (!message) return '';
  const lines = [];
  pushLine(lines, resolveReplyCopyLine(message?.reply));

  const format = String(message?.message_format || '').trim().toLowerCase();

  if (format === ADMIN_MESSAGE_FORMATS.TEXT) {
    pushLine(lines, message?.content);
    return lines.join('\n');
  }

  if (format === ADMIN_MESSAGE_FORMATS.IMAGE) {
    pushLine(lines, message?.content);
    if (!lines.length || lines[lines.length - 1] !== '[画像]') {
      pushLine(lines, '[画像]');
    }
    return lines.join('\n');
  }

  if (format === ADMIN_MESSAGE_FORMATS.FILE) {
    pushLine(lines, message?.content);
    const fileName =
      trimText(message?.original_name) ||
      trimText(message?.file_original_name) ||
      trimText(message?.stored_file_name) ||
      trimText(message?.file_file_name) ||
      'ファイル';
    pushLine(lines, `[ファイル] ${fileName}`);
    return lines.join('\n');
  }

  const title = resolveSystemFallbackTitle(message);
  const bodyText = resolveSystemBodyText(message);
  pushLine(lines, title);
  if (bodyText && bodyText !== title) {
    pushLine(lines, bodyText);
  }
  if (isAnnouncementEventFormat(format) || format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) {
    resolveEventMetaLines(message).forEach((line) => pushLine(lines, line));
  }
  return lines.join('\n');
};

export const hasCopyableChatMessageText = (message = {}) => resolveTextMessageContent(message).length > 0;

export const resolveCopySelectionShortcuts = (value) => {
  const text = normalizeText(value);
  return COPY_SHORTCUT_DEFINITIONS.map(({ key, pattern, resolveMatches }) => {
    if (typeof resolveMatches === 'function') {
      return {
        key,
        matches: resolveMatches(text),
      };
    }
    const matches = [];
    const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
    const regex = new RegExp(pattern.source, flags);
    let nextMatch = regex.exec(text);
    while (nextMatch) {
      const normalizedMatch = normalizeShortcutMatch(key, nextMatch[0], nextMatch.index);
      if (normalizedMatch) {
        matches.push(normalizedMatch);
      }
      nextMatch = regex.exec(text);
    }
    return {
      key,
      matches: uniqueMatches(matches),
    };
  });
};

export const writeTextToClipboard = async (value) => {
  const text = String(value ?? '');
  if (!trimText(text)) {
    throw new Error('コピーする内容がありません。');
  }

  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') {
    throw new Error('クリップボードへアクセスできません。');
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.left = '-1000px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error('コピーに失敗しました。');
  }
};
